import {
  inject,
  Injectable,
  Injector,
  PLATFORM_ID,
  TransferState
} from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  distinctUntilChanged,
  from,
  map,
  of,
  switchMap,
  tap
} from 'rxjs';
import { environment } from '~environments/environment';
import { Country } from '~core/models/shared.models';
import { SsrRequestService } from './ssr-request.service';
import { camelCase, isEqual, pickBy, toNumber } from 'lodash-es';
import {
  REMOTE_ENVIRONMENT_INJECTION_TOKEN,
  REMOTE_ENVIRONMENT_STATE_KEY
} from '~core/constants/global.constants';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { CountryCode } from 'libphonenumber-js';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable()
export class ConstantsService {
  protected _platformId = inject(PLATFORM_ID);
  protected _ssrRequestService = inject(SsrRequestService);
  protected _transferState = inject(TransferState);
  protected _injector = inject(Injector);

  private _environment = new BehaviorSubject<typeof environment>(environment);
  readonly environment$ = this._environment
    .asObservable()
    .pipe(distinctUntilChanged((a, b) => isEqual(a, b)));

  defaultCountry: CountryCode = 'CI';
  defaultCurrency: string = 'XOF';

  private _countriesSubject$ = new BehaviorSubject<Country[]>([]);

  get countries$(): Observable<Country[]> {
    return this._countriesSubject$.asObservable().pipe(
      switchMap(countries => {
        return countries.length ? of(countries) : this.getCountryList();
      })
    );
  }

  countries = toSignal(this.countries$);

  getCurrencyList(): Observable<string[]> {
    return from(
      fetch(
        `${this.environment.baseUrl}/assets/constants/currency-list.json`
      ).then(res => res.json())
    ).pipe(
      map(res => {
        if (typeof res === 'object' && 'currencyList' in res) {
          return res.currencyList;
        }
        return [];
      })
    );
  }

  getCountryList(): Observable<Country[]> {
    return from(
      fetch(
        `${this.environment.baseUrl}/assets/constants/country-list.json`
      ).then(res => res.json())
    ).pipe(
      map(res => {
        if (typeof res === 'object' && 'countryList' in res) {
          return res.countryList;
        }
        return [];
      }),
      tap(countries => {
        this._countriesSubject$.next(countries);
      })
    );
  }

  getReleaseInfo(): Observable<{ version: string; date: string }> {
    return from(
      fetch(
        `${this.environment.baseUrl}/assets/constants/release-version-info.json`
      ).then(res => res.json())
    ).pipe(
      map(res => {
        if (typeof res === 'object' && 'version' in res) {
          return {
            ...res,
            date: res.date || '',
            version: res.version
              .replace('console-', '')
              .replace('backoffice-', '')
          };
        }
        return { version: 'Dev', date: new Date().toLocaleDateString('fr-FR') };
      }),
      catchError(() =>
        of({ version: 'Dev', date: new Date().toLocaleDateString('fr-FR') })
      )
    );
  }

  /**
   *
   *
   * @param {string} countryCode
   * @return {*}  {(Observable<GeoJSON.Feature | null>)}
   * @memberof ConstantsService
   */
  getCountryGeoJson(countryCode: string): Observable<unknown | null> {
    return from(
      fetch(
        `${this.environment.baseUrl}/assets/constants/country-geo-json/${countryCode}.json`
      ).then(res => res.json())
    ).pipe(catchError(() => of(null)));
  }

  /**
   *
   * Get env configured in Docker compose
   * @return {*}  {Observable<typeof environment>}
   * @memberof ConstantsService
   */
  getRemoteEnvironmentVariables(): Observable<typeof environment> {
    if (this._isPlatformServer) {
      const ssrRemoteEnvironment = this._injector.get(
        REMOTE_ENVIRONMENT_INJECTION_TOKEN,
        null
      );
      if (ssrRemoteEnvironment) {
        this._transferState.set(
          REMOTE_ENVIRONMENT_STATE_KEY,
          ssrRemoteEnvironment
        );
        return of(ssrRemoteEnvironment as unknown as typeof environment).pipe(
          tap({
            next: res => {
              this._transferState.set(REMOTE_ENVIRONMENT_STATE_KEY, res);
              this._environment.next(this._mergeRemoteEnvironment(res));
            }
          })
        );
      }
    }

    if (this._isPlatformBrowser) {
      const remoteEnvironment = this._transferState.get(
        REMOTE_ENVIRONMENT_STATE_KEY,
        null
      );

      if (remoteEnvironment) {
        return of(remoteEnvironment as unknown as typeof environment).pipe(
          tap({
            next: res => {
              this._transferState.set(REMOTE_ENVIRONMENT_STATE_KEY, res);
              this._environment.next(this._mergeRemoteEnvironment(res));
            }
          })
        );
      }
    }

    return from(
      fetch(
        `${this._ssrRequestService.requestBaseUrl ?? ''}/variables.json`
      ).then(res => res.json())
    ).pipe(
      map(
        res =>
          ConstantsService.formatDockerLikeEnvironmentKeys(
            res
          ) as unknown as typeof environment
      ),
      tap({
        next: res => {
          this._transferState.set(REMOTE_ENVIRONMENT_STATE_KEY, res);
          this._environment.next(this._mergeRemoteEnvironment(res));
        }
      })
    );
  }

  protected _mergeRemoteEnvironment(
    remoteEnvironment: typeof environment
  ): typeof environment {
    return {
      ...environment,
      ...remoteEnvironment
    };
  }

  protected get _isPlatformServer(): boolean {
    return isPlatformServer(this._platformId);
  }

  protected get _isPlatformBrowser(): boolean {
    return isPlatformBrowser(this._platformId);
  }

  get environment(): typeof environment {
    return this._environment.getValue();
  }

  static formatDockerLikeEnvironmentKeys(
    env: Record<string, unknown>,
    prefix: string | undefined = 'APP_'
  ): Record<string, unknown> {
    const data = prefix ? pickBy(env, (_, key) => key.startsWith(prefix)) : env;
    return Object.keys(data).reduce(
      (acc, key) => {
        const newKey = camelCase(
          prefix ? key.replace(new RegExp(`^${prefix}`), '') : key
        );

        let value = data[key];
        const valueNumberCast = toNumber(value);
        if (value == 'true') {
          value = true;
        } else if (value == 'false') {
          value = false;
        } else if (!isNaN(valueNumberCast)) {
          value = valueNumberCast;
        }

        acc[newKey] = value;

        return acc;
      },
      {} as Record<string, unknown>
    );
  }
}
